import { useMemo, useState } from "react";
import type { CampaignDTO } from "@easyshop/shared";
import { Drawer, DrawerHeader, Empty, ErrorState, Loading } from "@/components/ui";
import { PromoInsights } from "@/components/promos/PromoInsights";
import { CampaignDetail, CampaignForm, OriginBadge } from "@/routes/Promos";
import { IosChevron, IosGroup, IosSection, IosSegmented, MobileActionButton } from "@/components/mobile/MobileUi";
import { useSession } from "@/hooks/useSession";
import { useSelectedBoutique } from "@/hooks/useSelectedBoutique";
import { useCampaigns } from "@/lib/queries";
import { dateFR } from "@/lib/format";

type View = "promos" | "analyse";
type FormState = { mode: "create" } | { mode: "edit"; campaign: CampaignDTO };

/** Jour local (minuit) d'une date ISO. */
function dayStart(iso: string | Date) {
  const d = new Date(iso);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/** Promos mobile : campagnes de la boutique choisie (en cours, à venir, passées) ou analyse de leur effet. */
export function MobilePromos() {
  const { selectedId } = useSelectedBoutique();
  const [view, setView] = useState<View>("promos");
  return (
    <div className="flex flex-col gap-[14px]">
      <IosSegmented<View>
        options={[
          { key: "promos", label: "Promos" },
          { key: "analyse", label: "Analyse" },
        ]}
        value={view}
        onChange={setView}
      />
      {selectedId && (view === "analyse" ? <PromoInsights key={selectedId} boutiqueIds={[selectedId]} compact /> : <PromosList boutiqueId={selectedId} />)}
    </div>
  );
}

function PromosList({ boutiqueId }: { boutiqueId: string }) {
  const { can } = useSession();
  const q = useCampaigns();
  const [openId, setOpenId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState | null>(null);

  const groups = useMemo(() => {
    const today = dayStart(new Date());
    const mine = (q.data ?? []).filter((c) => c.boutiques.some((b) => b.id === boutiqueId));
    const byStart = (a: CampaignDTO, b: CampaignDTO) => a.startsAt.localeCompare(b.startsAt);
    return [
      { key: "cours", title: "En cours", items: mine.filter((c) => dayStart(c.startsAt) <= today && dayStart(c.endsAt) >= today).sort(byStart) },
      { key: "venir", title: "À venir", items: mine.filter((c) => dayStart(c.startsAt) > today).sort(byStart) },
      // Passées : la plus récente d'abord.
      { key: "passees", title: "Passées", items: mine.filter((c) => dayStart(c.endsAt) < today).sort((a, b) => byStart(b, a)) },
    ];
  }, [q.data, boutiqueId]);

  const opened = (q.data ?? []).find((c) => c.id === openId) ?? null;
  const empty = groups.every((g) => g.items.length === 0);

  return (
    <>
      {can("campaigns.manage") && <MobileActionButton onClick={() => setForm({ mode: "create" })}>+ Nouvelle promo</MobileActionButton>}
      {q.isLoading && <Loading />}
      {q.error && <ErrorState error={q.error} />}
      {q.data && empty && <Empty>Aucune promo pour cette boutique.</Empty>}
      {groups
        .filter((g) => g.items.length > 0)
        .map((g) => (
          <IosSection key={g.key} title={`${g.title} (${g.items.length})`}>
            <IosGroup>
              {g.items.map((c) => (
                <PromoRow key={c.id} c={c} current={g.key === "cours"} onOpen={() => setOpenId(c.id)} />
              ))}
            </IosGroup>
          </IosSection>
        ))}

      <Drawer open={opened != null} onClose={() => setOpenId(null)} width={460}>
        {opened && (
          <>
            <DrawerHeader title="Promo" onClose={() => setOpenId(null)} />
            <CampaignDetail
              campaign={opened}
              onEdit={(c) => {
                setOpenId(null);
                setForm({ mode: "edit", campaign: c });
              }}
              onDeleted={() => setOpenId(null)}
            />
          </>
        )}
      </Drawer>
      <Drawer open={form != null} onClose={() => setForm(null)} width={460}>
        {form && (
          <CampaignForm
            key={form.mode === "edit" ? form.campaign.id : "new"}
            campaign={form.mode === "edit" ? form.campaign : undefined}
            defaultBoutiqueId={boutiqueId}
            onClose={() => setForm(null)}
            onSaved={(c) => {
              setForm(null);
              setOpenId(c.id);
            }}
          />
        )}
      </Drawer>
    </>
  );
}

/** Ligne d'une promo : nom, dates, origine, début de la description, fichiers joints. */
function PromoRow({ c, current, onOpen }: { c: CampaignDTO; current: boolean; onOpen: () => void }) {
  return (
    <button type="button" onClick={onOpen} className="flex w-full items-center gap-3 bg-white pl-4 text-left active:bg-[oklch(0.95_0.004_145)]">
      {current && <span aria-label="En cours" className="h-2 w-2 flex-shrink-0 rounded-full bg-positive" />}
      <span className="ios-sep flex min-w-0 flex-1 items-center gap-2 py-[11px] pr-4">
        <span className="flex min-w-0 flex-1 flex-col gap-[3px]">
          <span className="flex items-start gap-2">
            <span className="min-w-0 flex-1 break-words text-[16px] font-medium leading-[1.3]">{c.name}</span>
            {c.discountPct != null && (
              <span className="flex-shrink-0 rounded-[6px] bg-accent px-[7px] py-[2px] text-[12px] font-semibold text-white">-{c.discountPct} %</span>
            )}
          </span>
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] text-muted">
            <span>
              {dateFR(c.startsAt)} → {dateFR(c.endsAt)}
            </span>
            <OriginBadge c={c} />
          </span>
          {c.description && <span className="line-clamp-2 whitespace-pre-line text-[13px] leading-[1.4] text-ink-2">{c.description}</span>}
          {c.attachments.length > 0 && (
            <span className="text-[13px] text-accent">
              📎 {c.attachments.length} fichier{c.attachments.length > 1 ? "s" : ""}
            </span>
          )}
        </span>
        <IosChevron />
      </span>
    </button>
  );
}
